import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreditCard, FileText, Truck, CheckCircle2, ShieldCheck, Info, MapPin, Receipt, Smartphone, Landmark } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { formatINR, tierRate } from "@/lib/format";

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa",
  "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala",
  "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland",
  "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura",
  "Uttar Pradesh", "Uttarakhand", "West Bengal", "Delhi", "Jammu and Kashmir",
  "Ladakh", "Puducherry", "Chandigarh", "Andaman and Nicobar Islands",
  "Dadra and Nagar Haveli and Daman and Diu", "Lakshadweep",
];

const SHIPPING_METHODS = [
  {
    id: "self_pickup",
    title: "Self-Pickup (Tiruppur Warehouse)",
    desc: "Collect directly from our Tiruppur warehouse at zero shipping cost",
    charge: 0,
    badge: "Free",
  },
  {
    id: "transport",
    title: "Transport / Goods Carrier (To-Pay)",
    desc: "Dispatched via leading South India goods carrier. Freight charges payable to transporter upon delivery.",
    charge: 0,
    badge: "Pay on delivery",
  },
];

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Checkout() {
  const { items, clear } = useCart();
  const { user, config } = useAuth();
  const navigate = useNavigate();

  const sa = user?.address || {};
  const [addr, setAddr] = useState({
    attention: sa.attention || user?.contact_person || user?.company_name || "",
    country: sa.country || "India",
    street1: sa.street1 || "",
    street2: sa.street2 || "",
    city: sa.city || "",
    state: sa.state || "",
    zip: sa.zip || "",
    phone: sa.phone || user?.phone || "",
  });
  const [gstin, setGstin] = useState(user?.gstin || "");
  const [shippingMethod, setShippingMethod] = useState("transport");
  const set = (k) => (e) => setAddr((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));

  const [notes, setNotes] = useState("");
  const [transporter, setTransporter] = useState("");
  const [hub, setHub] = useState("");
  const [utr, setUtr] = useState("");
  const [placing, setPlacing] = useState(false);
  const [done, setDone] = useState(null);

  const selectedShipping = SHIPPING_METHODS.find((s) => s.id === shippingMethod) || SHIPPING_METHODS[1];
  const shippingCharge = selectedShipping.charge;

  const taxRate = config.tax_rate ?? 0.05;
  const codMax = config.cod_max ?? 20000;
  const codPct = config.cod_advance_pct ?? 0.2;
  const cardFeePct = config.card_fee_pct ?? 0.0241;
  const subtotal = items.reduce((s, i) => s + tierRate(i, i.quantity) * i.quantity, 0);
  const intra = (addr.state || "").toLowerCase() === (config.home_state || "Tamil Nadu").toLowerCase();
  const r2 = (n) => Math.round(n * 100) / 100;
  const halfTax = r2((subtotal * taxRate) / 2);
  const gstAmount = intra ? halfTax * 2 : r2(subtotal * taxRate);
  const total = subtotal + gstAmount + shippingCharge;
  const minOrder = config.min_order_value ?? 6000;
  const belowMin = subtotal < minOrder;
  const bank = config.bank_details || {};

  const codAllowed = config.razorpay_enabled && total <= codMax;
  const codAdvance = Math.round(total * codPct * 100) / 100;
  const cardFee = Math.round(total * cardFeePct * 100) / 100;
  const cardTotal = total + cardFee;

  const methods = [
    config.razorpay_enabled && { id: "razorpay", enabled: true },
    config.razorpay_enabled && { id: "card", enabled: true },
    { id: "cod", enabled: codAllowed },
    { id: "neft", enabled: true },
    user?.allow_pay_later === true && { id: "paylater", enabled: true },
  ].filter(Boolean);
  const [method, setMethod] = useState(config.razorpay_enabled ? "razorpay" : "neft");

  const placeOrder = async () => {
    if (belowMin) return toast.error(`Minimum order value is ${formatINR(minOrder)} (before GST).`);
    if (!addr.street1.trim()) return toast.error("Please enter the street address.");
    if (!addr.city.trim() || !addr.state || !addr.zip.trim()) return toast.error("Please enter city, state and pin code.");
    if (!addr.phone.trim()) return toast.error("Please enter a contact phone.");
    if (method === "cod" && !codAllowed) return toast.error("Cash on Delivery is not available for this order.");
    if (shippingMethod === "transport" && (!transporter.trim() || !hub.trim())) return toast.error("Please enter the transporter name and godown / hub branch.");
    if (method === "neft" && !/^[A-Z0-9]{6,30}$/.test(utr.replace(/\s/g, ""))) return toast.error("Please enter the UTR / reference number of your bank transfer.");
    setPlacing(true);
    try {
      const { data } = await api.post("/orders", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        payment_method: method,
        shipping_method: shippingMethod,
        shipping_charge: shippingCharge,
        gstin: gstin.trim(),
        notes,
        transporter_name: shippingMethod === "transport" ? transporter.trim() : "",
        hub_branch: shippingMethod === "transport" ? hub.trim() : "",
        utr: method === "neft" ? utr.replace(/\s/g, "") : "",
        address: addr,
      });

      if (["razorpay", "card", "cod"].includes(method) && data.razorpay_order) {
        const ok = await loadRazorpay();
        if (!ok) throw new Error("Could not load payment gateway.");
        const rzpOptions = {
          key: data.razorpay_key_id,
          amount: data.razorpay_order.amount,
          currency: data.razorpay_order.currency,
          order_id: data.razorpay_order.id,
          name: config.business_name || "Zealous Ventures",
          description: method === "cod" ? `${data.order.order_number} · COD advance` : method === "card" ? `${data.order.order_number} · Card (incl. processing charge)` : data.order.order_number,
          prefill: { name: user.company_name, email: user.email, contact: user.phone },
          theme: { color: "#111827" },
          handler: async (resp) => {
            await api.post("/payments/verify", {
              order_id: data.order.id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            clear();
            setDone({ ...data.order, _method: method });
            toast.success(method === "cod" ? "Advance paid — order confirmed!" : "Payment successful!");
          },
        };
        if (method === "card") {
          rzpOptions.config = { display: { blocks: { card: { name: "Pay by Card", instruments: [{ method: "card" }] } }, sequence: ["block.card"], preferences: { show_default_blocks: false } } };
        } else if (method === "razorpay") {
          rzpOptions.config = { display: { blocks: { upi_nb: { name: "Pay via UPI / NetBanking", instruments: [{ method: "upi" }, { method: "netbanking" }] } }, sequence: ["block.upi_nb"], preferences: { show_default_blocks: false } } };
        }
        const rzp = new window.Razorpay(rzpOptions);
        rzp.open();
      } else {
        clear();
        setDone({ ...data.order, _method: method });
        toast.success("Order placed successfully.");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setPlacing(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="max-w-lg mx-auto px-4 py-20 text-center" data-testid="order-success">
          <CheckCircle2 className="h-16 w-16 mx-auto text-emerald-500 mb-4" />
          <h1 className="font-heading text-3xl font-bold text-slate-900">{done._method === "neft" ? "Order received" : "Order confirmed"}</h1>
          <p className="text-slate-500 mt-2">Order <span className="font-mono-num font-semibold text-slate-900">{done.order_number}</span> has been placed successfully.</p>
          {done._method === "neft" && (
            <p className="text-sm text-slate-500 mt-3" data-testid="neft-pending-note">We'll confirm your order as soon as we verify your bank transfer (UTR {done.utr}).</p>
          )}
          {done._method === "paylater" && (
            <p className="text-sm text-slate-500 mt-3">Our sales team will contact you shortly regarding payment / credit terms for this order.</p>
          )}
          {done._method === "cod" && (
            <p className="text-sm text-slate-500 mt-3">Advance paid. The balance is payable on delivery.</p>
          )}
          <div className="mt-8 flex gap-3 justify-center">
            <Button onClick={() => navigate("/account")} className="rounded-full bg-slate-900 hover:bg-black text-white" data-testid="view-orders-btn">View my orders</Button>
            <Button onClick={() => navigate("/")} variant="outline" className="rounded-full">Continue shopping</Button>
          </div>
        </div>
      </div>
    );
  }

  const methodMeta = {
    razorpay: { icon: Smartphone, title: "UPI / NetBanking", desc: "No extra charge" },
    card: { icon: CreditCard, title: "Credit / Debit Card", desc: `Card processing charge ${(cardFeePct * 100).toFixed(2)}% (${formatINR(cardFee)}) applies` },
    cod: { icon: Truck, title: "Cash on Delivery", desc: `${Math.round(codPct * 100)}% advance (${formatINR(codAdvance)}) now, balance on delivery` },
    neft: { icon: Landmark, title: "NEFT / RTGS bank transfer", desc: "Transfer to our bank account and enter the UTR — order confirms after we verify" },
    paylater: { icon: FileText, title: "Pay Later", desc: `Approved credit${user?.payment_terms ? ` · Net ${user.payment_terms} days` : ""} — pay against invoice` },
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <h1 className="font-heading text-3xl font-bold text-slate-900 mb-8">Checkout</h1>
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {/* ADDRESS & GST */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                <MapPin className="h-5 w-5 text-slate-700" />
                <h2 className="font-heading font-semibold text-lg">Billing &amp; shipping address</h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label>Attention</Label>
                  <Input data-testid="addr-attention" value={addr.attention} onChange={set("attention")} className="mt-1.5" placeholder="Contact / business name" />
                </div>
                <div>
                  <Label>Country / Region</Label>
                  <Input data-testid="addr-country" value={addr.country} onChange={set("country")} className="mt-1.5" />
                </div>
                <div>
                  <Label>Phone <span className="text-rose-500">*</span></Label>
                  <Input data-testid="addr-phone" value={addr.phone} onChange={set("phone")} className="mt-1.5" placeholder="+91 ..." />
                </div>
                <div className="sm:col-span-2">
                  <Label>Street 1 <span className="text-rose-500">*</span></Label>
                  <Input data-testid="addr-street1" value={addr.street1} onChange={set("street1")} className="mt-1.5" placeholder="Building, street, door no." />
                </div>
                <div className="sm:col-span-2">
                  <Label>Street 2</Label>
                  <Input data-testid="addr-street2" value={addr.street2} onChange={set("street2")} className="mt-1.5" placeholder="Area, landmark (optional)" />
                </div>
                <div>
                  <Label>City <span className="text-rose-500">*</span></Label>
                  <Input data-testid="addr-city" value={addr.city} onChange={set("city")} className="mt-1.5" />
                </div>
                <div>
                  <Label>State <span className="text-rose-500">*</span></Label>
                  <Select value={addr.state} onValueChange={set("state")}>
                    <SelectTrigger className="mt-1.5" data-testid="addr-state"><SelectValue placeholder="Select state" /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {INDIAN_STATES.map((s) => (
                        <SelectItem key={s} value={s} data-testid={`state-option-${s}`}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Pin Code <span className="text-rose-500">*</span></Label>
                  <Input data-testid="addr-zip" value={addr.zip} onChange={set("zip")} className="mt-1.5" placeholder="641603" />
                </div>
                <div>
                  <Label className="flex items-center gap-1.5">
                    <Receipt className="h-3.5 w-3.5 text-slate-500" />
                    GSTIN <span className="text-slate-400 font-normal text-xs">(Optional — for tax invoice/ITC)</span>
                  </Label>
                  <Input
                    data-testid="checkout-gstin"
                    value={gstin}
                    onChange={(e) => setGstin(e.target.value.toUpperCase())}
                    className="mt-1.5 uppercase font-mono-num"
                    placeholder="33AAAAA0000A1Z5"
                    maxLength={15}
                  />
                </div>
              </div>
              <Label className="mt-4 block">Order notes (optional)</Label>
              <Textarea data-testid="checkout-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-1.5" placeholder="Delivery instructions, PO number..." />
            </div>

            {/* SHIPPING METHOD */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                <Truck className="h-5 w-5 text-slate-700" />
                <h2 className="font-heading font-semibold text-lg">Shipping method</h2>
              </div>
              <div className="space-y-3">
                {SHIPPING_METHODS.map((sm) => (
                  <button
                    key={sm.id}
                    type="button"
                    onClick={() => setShippingMethod(sm.id)}
                    data-testid={`ship-${sm.id}`}
                    className={`w-full flex items-center justify-between p-4 rounded-xl border text-left transition-colors ${
                      shippingMethod === sm.id ? "border-slate-900 bg-yellow-50/70" : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`mt-0.5 h-4 w-4 rounded-full border flex items-center justify-center ${shippingMethod === sm.id ? "border-slate-900 bg-slate-900" : "border-slate-300"}`}>
                        {shippingMethod === sm.id && <div className="h-1.5 w-1.5 rounded-full bg-white" />}
                      </div>
                      <div>
                        <div className="font-medium text-slate-900">{sm.title}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{sm.desc}</div>
                      </div>
                    </div>
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-800 shrink-0 ml-3">
                      {sm.badge}
                    </span>
                  </button>
                ))}
                {shippingMethod === "transport" && (
                  <div className="ml-7 grid sm:grid-cols-2 gap-3 rounded-xl bg-slate-50 border border-slate-100 p-4" data-testid="transport-fields">
                    <div>
                      <Label>Transporter Name <span className="text-rose-500">*</span></Label>
                      <Input data-testid="transporter-name" value={transporter} onChange={(e) => setTransporter(e.target.value)} className="mt-1.5 bg-white" placeholder="e.g. KPN Parcel Service" />
                    </div>
                    <div>
                      <Label>Godown / Hub Branch <span className="text-rose-500">*</span></Label>
                      <Input data-testid="hub-branch" value={hub} onChange={(e) => setHub(e.target.value)} className="mt-1.5 bg-white" placeholder="e.g. Madurai – Kalavasal" />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* PAYMENT METHOD */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                <CreditCard className="h-5 w-5 text-slate-700" />
                <h2 className="font-heading font-semibold text-lg">Payment method</h2>
              </div>
              <div className="space-y-3">
                {methods.map(({ id, enabled }) => {
                  const m = methodMeta[id];
                  const Icon = m.icon;
                  return (
                    <button
                      key={id}
                      disabled={!enabled}
                      onClick={() => enabled && setMethod(id)}
                      data-testid={`pay-${id}`}
                      className={`w-full flex items-center gap-3 p-4 rounded-xl border text-left transition-colors ${
                        method === id ? "border-slate-900 bg-yellow-50" : "border-slate-200"
                      } ${!enabled ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                      <Icon className="h-5 w-5 text-slate-700" />
                      <div>
                        <div className="font-medium text-slate-900">{m.title}</div>
                        <div className="text-xs text-slate-500">{m.desc}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
              {method === "neft" && (
                <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3" data-testid="neft-details">
                  <div className="text-sm font-semibold text-slate-900 flex items-center gap-2"><Landmark className="h-4 w-4" /> Bank transfer details</div>
                  <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1 text-sm">
                    <dt className="text-slate-500">A/C Name</dt><dd className="font-medium" data-testid="bank-acname">{bank.account_name}</dd>
                    <dt className="text-slate-500">Bank</dt><dd className="font-medium">{bank.bank}</dd>
                    <dt className="text-slate-500">A/C No</dt><dd className="font-mono-num font-semibold" data-testid="bank-acno">{bank.account_number}</dd>
                    <dt className="text-slate-500">IFSC</dt><dd className="font-mono-num font-semibold" data-testid="bank-ifsc">{bank.ifsc}</dd>
                    <dt className="text-slate-500">Branch</dt><dd className="font-medium">{bank.branch}</dd>
                  </dl>
                  <div>
                    <Label>UTR / Reference No <span className="text-rose-500">*</span></Label>
                    <Input data-testid="neft-utr" value={utr} onChange={(e) => setUtr(e.target.value.toUpperCase())} className="mt-1.5 bg-white font-mono-num uppercase" placeholder="e.g. AXISN52025102212345" maxLength={30} />
                    <p className="text-xs text-slate-400 mt-1">Transfer {formatINR(total)}, then enter the UTR. We confirm your order after verifying the payment.</p>
                  </div>
                </div>
              )}
              {!config.razorpay_enabled && (
                <div className="mt-4 text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 flex items-center gap-1.5">
                  <ShieldCheck className="h-3.5 w-3.5" /> Online payment &amp; COD activate once Razorpay keys are added.
                </div>
              )}
              {config.razorpay_enabled && total > codMax && (
                <div className="mt-4 text-xs text-slate-500 bg-slate-50 rounded-lg px-3 py-2 flex items-center gap-1.5" data-testid="cod-limit-note">
                  <Info className="h-3.5 w-3.5" /> Cash on Delivery is available only for orders up to {formatINR(codMax)} (shipping cost is high beyond this).
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl border border-slate-200 p-6 sticky top-24">
              <h2 className="font-heading font-bold text-lg mb-4">Summary</h2>
              <div className="space-y-2 text-sm max-h-52 overflow-auto mb-3">
                {items.map((i) => (
                  <div key={i.product_id} className="flex justify-between gap-2">
                    <span className="text-slate-500 truncate">{i.name} × {i.quantity}</span>
                    <span className="font-mono-num shrink-0">{formatINR(tierRate(i, i.quantity) * i.quantity)}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-slate-100 pt-3 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="font-mono-num">{formatINR(subtotal)}</span></div>
                {intra ? (
                  <>
                    <div className="flex justify-between" data-testid="tax-cgst-row"><span className="text-slate-500">CGST ({(taxRate * 50).toFixed(1)}%)</span><span className="font-mono-num">{formatINR(halfTax)}</span></div>
                    <div className="flex justify-between" data-testid="tax-sgst-row"><span className="text-slate-500">SGST ({(taxRate * 50).toFixed(1)}%)</span><span className="font-mono-num">{formatINR(halfTax)}</span></div>
                  </>
                ) : (
                  <div className="flex justify-between" data-testid="tax-igst-row"><span className="text-slate-500">IGST ({(taxRate * 100).toFixed(1)}%)</span><span className="font-mono-num">{formatINR(gstAmount)}</span></div>
                )}
                <div className="flex justify-between" data-testid="shipping-charge-row">
                  <span className="text-slate-500">Shipping ({selectedShipping.title.split(" ")[0]})</span>
                  <span className="font-mono-num">{shippingCharge > 0 ? formatINR(shippingCharge) : "Free / To-Pay"}</span>
                </div>
                <div className="flex justify-between text-base pt-1 border-t border-slate-100"><span className="font-semibold">Total</span><span className="font-mono-num font-bold" data-testid="checkout-total">{formatINR(total)}</span></div>
                {method === "card" && (
                  <>
                    <div className="flex justify-between text-xs text-slate-500 pt-1" data-testid="card-fee-row">
                      <span>Card processing charge ({(cardFeePct * 100).toFixed(2)}%)</span><span className="font-mono-num">{formatINR(cardFee)}</span>
                    </div>
                    <div className="flex justify-between text-sm pt-1" data-testid="card-payable-row">
                      <span className="font-semibold text-slate-900">Payable by card</span><span className="font-mono-num font-bold text-slate-900">{formatINR(cardTotal)}</span>
                    </div>
                  </>
                )}
                {method === "cod" && (
                  <div className="flex justify-between text-xs text-slate-500 pt-1" data-testid="cod-advance-row">
                    <span>Advance payable now ({Math.round(codPct * 100)}%)</span><span className="font-mono-num">{formatINR(codAdvance)}</span>
                  </div>
                )}
              </div>
              {belowMin && (
                <div className="mt-4 text-xs text-amber-800 bg-amber-50 rounded-lg px-3 py-2" data-testid="checkout-min-order-warning">
                  Minimum order value is {formatINR(minOrder)} (before GST). Add {formatINR(Math.round((minOrder - subtotal) * 100) / 100)} more.
                </div>
              )}
              <Button onClick={placeOrder} disabled={placing || items.length === 0 || belowMin} className="w-full mt-5 h-12 rounded-full bg-[#FFDA00] hover:bg-[#f2ce00] text-slate-900 font-semibold disabled:opacity-50" data-testid="checkout-place-order-button">
                {placing ? "Processing..." : method === "razorpay" ? `Pay ${formatINR(total)}` : method === "card" ? `Pay ${formatINR(cardTotal)}` : method === "cod" ? `Pay ${Math.round(codPct * 100)}% Advance (${formatINR(codAdvance)})` : method === "neft" ? "Submit order & UTR" : "Place order"}
              </Button>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
